import { z } from 'zod'
import { apiClient } from '@/shared/api/apiClient'
import { emissionQualifyingParamsSchema } from '@/shared/api/EmissionQualifyingParamsModel'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SelectedElectricityProductionMethod } from '@/features/production-routes/api/ElectricityProductionMethodsModel'
import {
  DataQualityInfo,
  JustificationInfo,
  QualityAssuranceInfo
} from '@/features/dashboard/pages/finalized-reports/finalized-report-page/constants'

const REPORTS_QUERY_KEY = 'finalizedReports'

const aggregatedElectricitySourceSchema = z.object({
  type: z.string(),
  weightedAvgEmissionFactor: z.number(),
  totalConsumption: z.number(),
  totalConsumptionShare: z.number(),
  isRenewable: z.boolean(),
  isEmissionFactorDefault: z.boolean(),
  info: z.string().nullable()
})

const aggregatedElectricitySchema = z.object({
  combinedEmissionFactor: z.number(),
  sources: z.array(aggregatedElectricitySourceSchema)
})

export type AggregatedElectricity = z.infer<typeof aggregatedElectricitySchema>

const methodAggregatedEmissionSchema = z.object({
  productionMethodCode: z.string(),
  activityLevel: z.number(),

  cnCodes: z.array(z.string()),
  specificEmbeddedDirect: z.number(),
  specificEmbeddedIndirect: z.number(),

  combinedElectricityConsumptionMWh: z.number(),
  combinedSpecificElectricityConsumption: z.number(),
  combinedElectricityEmissionFactor: z.number(),

  onSiteElectricityConsumptionMWh: z.number(),
  onSiteSpecificElectricityConsumption: z.number(),
  onSiteSpecificElectricityEmissions: z.number(),
  onSiteElectricityCombinedEmissionFactor: z.number(),

  precursorSpecificIndirectEmissions: z.number(),
  precursorElectricityCombinedEmissionFactor: z.number(),
  precursorElectricityConsumptionPerTofProduct: z.number(),
  precursorElectricityConsumptionMWh: z.number()
})

export type MethodAggregatedEmissions = z.infer<typeof methodAggregatedEmissionSchema>

const monitoringMethodologySchema = z.object({
  isMonitoringMethodologyCalculationBased: z.boolean().nullable().optional(),
  monitoringMethodologyCalculationBasedInfo: z.string().nullable().optional(),
  isMonitoringMethodologyMeasurementBased: z.boolean().nullable().optional(),
  monitoringMethodologyMeasurementBasedInfo: z.string().nullable().optional(),
  isMonitoringMethodologyOther: z.boolean().nullable().optional(),
  monitoringMethodologyOtherInfo: z.string().nullable().optional()
})

const finalizedReportCreatePayloadSchema = z.object({
  installationId: z.string(),
  productionRouteId: z.string(),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  methodAggregatedEmissions: z.array(methodAggregatedEmissionSchema),
  emissionQualifyingParams: emissionQualifyingParamsSchema.partial(),
  dataQualityInfo: z.string().nullable(),
  qualityAssuranceInfo: z.string().nullable(),
  defaultValuesJustification: z.array(z.string()).nullable().optional(),
  aggregatedElectricity: aggregatedElectricitySchema,
  ...monitoringMethodologySchema.shape
})

const finalizedReportUpdatePayloadSchema = z.object({
  isMonitoringMethodologyCalculationBased: z.boolean().nullable().optional(),
  monitoringMethodologyCalculationBasedInfo: z.string().nullable().optional(),
  isMonitoringMethodologyMeasurementBased: z.boolean().nullable().optional(),
  monitoringMethodologyMeasurementBasedInfo: z.string().nullable().optional(),
  isMonitoringMethodologyOther: z.boolean().nullable().optional(),
  monitoringMethodologyOtherInfo: z.string().nullable().optional(),
  emissionQualifyingParams: emissionQualifyingParamsSchema.partial(),
  dataQualityInfo: z.string().nullable(),
  qualityAssuranceInfo: z.string().nullable(),
  defaultValuesJustification: z.array(z.string()).nullable().optional()
})

export enum FinalizedReportStatus {
  PENDING = 'PENDING',
  SUBMITTED = 'SUBMITTED'
}

export const finalizedReportSchema = finalizedReportCreatePayloadSchema.extend({
  ...finalizedReportUpdatePayloadSchema.shape,
  id: z.string(),
  createdAt: z.iso.date(),
  updatedAt: z.iso.date().nullable(),
  finalizedAt: z.iso.date().nullable(),
  status: z.enum(FinalizedReportStatus),
  version: z.string()
})

export type FinalizedReport = z.infer<typeof finalizedReportSchema>

export type FinalizedReportPreview = FinalizedReport & {
  installation: {
    name: string
    street: string
    street2: string
    streetNumber: string
    city: string
    postCode: string
    country: string
    longitude: string
    latitude: string
    basisForReportingPeriod: string
    unlocode: string
    // local/native fields (optional)
    localName?: string
    localStreet?: string
    localStreet2?: string
    localStreetNumber?: string
    localPostCode?: string
    localPoBox?: string
    localCity?: string
  }
  productionRoute: {
    name: string
    aggregatedGood: string
    productionMethods: {
      code: string
      cnCodes: string[]
    }[]
    electricityProductionMethods: SelectedElectricityProductionMethod[]
  }
  aggregatedGood: string
  methodAggregatedEmissions: MethodAggregatedEmissions[]
  createdAt: string
  startDate: string
  endDate: string
  electricity: AggregatedElectricity
  isMonitoringMethodologyCalculationBased: boolean
  isMonitoringMethodologyMeasurementBased: boolean
  isMonitoringMethodologyOther: boolean
  monitoringMethodologyCalculationBasedInfo: string
  monitoringMethodologyMeasurementBasedInfo: string
  monitoringMethodologyOtherInfo: string
  dataQualityInfo: DataQualityInfo
  qualityAssuranceInfo: QualityAssuranceInfo
  defaultValuesJustification: JustificationInfo[]
}

export const useSubmitFinalizedReport = (
  organizationId: string,
  installationId: string,
  finalizedReportId: string
) => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (args: { lang?: string }) => {
      const { lang } = args
      const response = await apiClient.post<FinalizedReport>(
        `/v1/organizations/${organizationId}/installations/${installationId}/finalized-reports/${finalizedReportId}/submit`,
        {},
        {
          params: { lang },
          timeout: 60000
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [REPORTS_QUERY_KEY, organizationId, installationId]
      })
    }
  })

  return mutation
}
